<script>
/**
 * @component ContractDetail
 * @desc Detalle de un contrato médico. Permite editar datos generales
 *       y gestionar las prestaciones con sus precios.
 *       Ruta /contracts/new para crear, /contracts/:id para editar.
 */
import medicalContractService from "@/services/medical-contract";
import practiceCatalogService from "@/services/practice-catalog";
import doctorService from "@/services/doctor";
import planService from "@/services/plan";
import { mapGetters } from "vuex";
import debounce from "debounce";

const DEBOUNCE_MS = 400;

export default {
  name: "ContractDetail",
  computed: {
    ...mapGetters(["isAdmin"]),
    /** @returns {boolean} Si es creación nueva */
    isNew() {
      return this.$route.name === "contract-new";
    },
    /** @returns {string} ID del contrato */
    contractId() {
      return this.$route.params.id;
    },
    /**
     * @description UC-REG-164 — Agrupa items por practice._id para tabla
     *   expandible. Cada grupo tiene metadata compartida + array de items
     *   uno por plan. Cuando sameValueForAllPlans=true los grupos tienen
     *   1 solo item con plan=null (comportamiento antiguo).
     * @returns {Array}
     */
    groupedItems() {
      const groups = new Map();
      // UC-REG-203 — filterPlan: filtra items por plan._id antes de agrupar.
      // Grupos sin items sobrevivientes quedan fuera.
      const planFilter = this.filterPlan || null;
      this.items.forEach((item) => {
        if (!item.practice) return;
        if (planFilter) {
          const itemPlanId = item.plan && (item.plan._id || item.plan);
          // UC-REG-228 — Al filtrar por un plan (ej. Oro) traer también los
          //   items sin plan, que aplican a TODOS los planes. Solo se
          //   descartan los items de OTRO plan específico distinto al elegido.
          if (itemPlanId && String(itemPlanId) !== String(planFilter)) return;
        }
        const key = String(item.practice._id);
        if (!groups.has(key)) {
          groups.set(key, {
            practiceId: item.practice._id,
            code: item.practice.code,
            name: item.practice.name,
            providerCode: item.providerCode || null,
            providerName: item.providerName || null,
            plans: [],
          });
        }
        groups.get(key).plans.push(item);
      });
      return [...groups.values()];
    },
    /**
     * @description UC-REG-164 — En modo por-plan (false), la tabla se
     *   muestra expandible con detalle por plan. En true, tabla plana.
     * @returns {boolean}
     */
    perPlanContract() {
      return this.form.sameValueForAllPlans === false;
    },
  },
  data() {
    return {
      activeTab: "general",
      loading: false,
      saving: false,
      contract: null,
      form: {
        provider: "",
        contractNumber: "",
        status: "draft",
        startsAt: "",
        endsAt: "",
        paymentModality: "per-practice",
        sameValueForAllPlans: true,
        // UC-REG-163 — 'ours' | 'provider'. Default nuestros códigos.
        codeDisplayMode: "ours",
        notes: "",
      },
      formErrors: {},
      providerOptions: [],
      providerSearchLoading: false,
      // Prestaciones
      items: [],
      itemsTotal: 0,
      itemsPage: 1,
      itemsPageSize: 20,
      itemsLoading: false,
      itemsSearch: "",
      // UC-REG-203 — Filtro por plan para la vista Prestaciones.
      filterPlan: "",
      // Dialog agregar/editar prestación
      itemDialogVisible: false,
      // UC-REG-163 — Si es null, el modal está en modo "Agregar"; si tiene
      // el _id de un item, está en modo "Editar" (bloquea la práctica y
      // oculta la sección de precio inicial).
      editingItemId: null,
      itemForm: {
        practice: "",
        practiceLabel: "",
        isEnabled: true,
        // UC-REG-163 — Código/nombre según el prestador (opcional).
        providerCode: "",
        providerName: "",
        // UC-REG-164 — Plan al que aplica el item (null = todos).
        // Solo se pide en el modal cuando sameValueForAllPlans=false.
        plan: null,
        amount: 0,
        effectiveFrom: new Date().toISOString().split("T")[0],
        hasCopago: false,
        copagoAmount: 0,
        hasCoseguro: false,
        coseguroPercent: 0,
      },
      itemFormErrors: {},
      practiceOptions: [],
      practiceSearchLoading: false,
      itemSaving: false,
      // UC-REG-164 — Planes disponibles para el selector del modal
      // (solo cuando sameValueForAllPlans=false). Se cargan una vez al
      // montar el componente.
      planOptions: [],
      // UC-REG-164 — Estado de expandido por practiceId en la vista
      // por-plan. Reemplaza el type="expand" de Element UI que renderizaba
      // el slot dentro de la primera celda en vez de en fila hija.
      expandedGroups: {},
      // Dialog agregar precio
      priceDialogVisible: false,
      selectedItem: null,
      priceForm: {
        amount: 0,
        effectiveFrom: new Date().toISOString().split("T")[0],
        hasCopago: false,
        copagoAmount: 0,
        hasCoseguro: false,
        coseguroPercent: 0,
      },
      priceSaving: false,
    };
  },
  created() {
    if (!this.isNew) this.fetchContract();
    this.loadPlans();
  },
  methods: {
    /**
     * @method fetchContract
     * @desc Carga el contrato y sus datos generales
     */
    async fetchContract() {
      this.loading = true;
      try {
        this.contract = await medicalContractService.getById(this.contractId);
        this.form = {
          provider: this.contract.provider?._id || this.contract.provider,
          contractNumber: this.contract.contractNumber || "",
          status: this.contract.status,
          startsAt: this.contract.startsAt
            ? this.contract.startsAt.split("T")[0]
            : "",
          endsAt: this.contract.endsAt
            ? this.contract.endsAt.split("T")[0]
            : "",
          paymentModality: this.contract.paymentModality,
          sameValueForAllPlans: this.contract.sameValueForAllPlans,
          // UC-REG-163 — default 'ours' si el contrato es previo al feature.
          codeDisplayMode: this.contract.codeDisplayMode || "ours",
          notes: this.contract.notes || "",
        };
        if (this.contract.provider) {
          this.providerOptions = [
            {
              value: this.contract.provider._id,
              label: `${this.contract.provider.firstName} ${this.contract.provider.lastName}`,
            },
          ];
        }
      } catch {
        this.$message.error("Error al cargar el contrato");
      } finally {
        this.loading = false;
      }
    },

    /**
     * @method fetchItems
     * @desc Carga las prestaciones del contrato con paginación y búsqueda
     */
    async fetchItems() {
      this.itemsLoading = true;
      try {
        const params = {
          offset: (this.itemsPage - 1) * this.itemsPageSize,
          limit: this.itemsPageSize,
        };
        if (this.itemsSearch) params.searchInput = this.itemsSearch;
        const result = await medicalContractService.getItems(
          this.contractId,
          params
        );
        this.items = result.docs || [];
        this.itemsTotal = result.total || 0;
      } catch {
        this.$message.error("Error al cargar prestaciones");
      } finally {
        this.itemsLoading = false;
      }
    },

    debounceItemsSearch: debounce(function () {
      this.itemsPage = 1;
      this.fetchItems();
    }, DEBOUNCE_MS),

    /**
     * @method onTabChange
     * @desc Al cambiar a tab prestaciones, carga los items
     * @param {Object} tab - Tab seleccionado
     */
    onTabChange(tab) {
      if (tab === "items" && !this.isNew) this.fetchItems();
    },

    /**
     * @method onTabClick
     * @desc Handler para el evento tab-click de Element UI
     * @param {Object} tab - Instancia del tab clickeado
     */
    onTabClick(tab) {
      this.onTabChange(tab.name);
    },

    /**
     * @method saveContract
     * @desc Crea o actualiza el contrato
     */
    async saveContract() {
      this.formErrors = {};
      if (!this.form.provider) this.formErrors.provider = "Requerido";
      if (!this.form.startsAt) this.formErrors.startsAt = "Requerido";
      if (Object.keys(this.formErrors).length) return;

      this.saving = true;
      try {
        if (this.isNew) {
          const contract = await medicalContractService.create(this.form);
          this.$message.success("Contrato creado");
          this.$router.replace({
            name: "contract-detail",
            params: { id: contract._id },
          });
        } else {
          await medicalContractService.update(this.contractId, this.form);
          this.$message.success("Contrato actualizado");
        }
      } catch {
        this.$message.error("Error al guardar el contrato");
      } finally {
        this.saving = false;
      }
    },

    /**
     * @method searchProviders
     * @desc Búsqueda remota de prestadores para el selector
     * @param {string} query - Texto de búsqueda
     */
    async searchProviders(query) {
      if (!query) return;
      this.providerSearchLoading = true;
      try {
        const doctors = await doctorService.getDoctors({ search: query });
        this.providerOptions = (
          Array.isArray(doctors) ? doctors : doctors.docs || []
        ).map((d) => ({
          value: d._id,
          label: `${d.firstName} ${d.lastName}`,
        }));
      } finally {
        this.providerSearchLoading = false;
      }
    },

    /**
     * @method searchPractices
     * @desc Búsqueda remota de prestaciones para el selector del dialog
     * @param {string} query - Texto de búsqueda
     */
    async searchPractices(query) {
      if (!query) return;
      this.practiceSearchLoading = true;
      try {
        const result = await practiceCatalogService.query({
          searchInput: query,
          limit: 20,
        });
        this.practiceOptions = (result.docs || []).map((p) => ({
          value: p._id,
          label: `${p.code} - ${p.name}`,
        }));
      } finally {
        this.practiceSearchLoading = false;
      }
    },

    /**
     * @method openItemDialog
     * @desc Abre el dialog para agregar una prestación
     */
    openItemDialog(prefill) {
      this.editingItemId = null;
      const p = prefill || {};
      this.itemForm = {
        // UC-REG-164 — Prefill se usa cuando el usuario abre desde el
        // botón "+ Agregar plan" dentro de una práctica ya cargada:
        // pre-fija la práctica y sus providerCode/Name pero deja el
        // plan vacío para que elija uno nuevo.
        practice: p.practiceId || "",
        practiceLabel: p.practiceLabel || "",
        isEnabled: true,
        providerCode: p.providerCode || "",
        providerName: p.providerName || "",
        plan: null,
        amount: 0,
        effectiveFrom: new Date().toISOString().split("T")[0],
        hasCopago: false,
        copagoAmount: 0,
        hasCoseguro: false,
        coseguroPercent: 0,
      };
      this.practiceOptions = p.practiceId
        ? [{ value: p.practiceId, label: p.practiceLabel }]
        : [];
      this.itemFormErrors = {};
      this.itemDialogVisible = true;
    },

    /**
     * @method loadPlans
     * @desc UC-REG-164 — Carga los planes de la organización para el
     *   selector del modal cuando sameValueForAllPlans=false.
     */
    async loadPlans() {
      try {
        const res = await planService.query({ limit: 200 });
        this.planOptions = (res.docs || res || []).map((p) => ({
          value: p._id,
          label: p.name,
        }));
      } catch (e) {
        // Non-fatal — el selector queda vacío pero el resto del modal
        // funciona igual (el usuario ve el error al intentar guardar).
      }
    },

    /**
     * @method openEditItemDialog
     * @desc UC-REG-163 — Abre el modal en modo edición pre-poblado con
     *       los datos actuales del item. La práctica queda bloqueada
     *       (para cambiarla, borrar y crear). No se muestra la sección
     *       "Precio inicial" — los precios tienen su propia gestión.
     * @param {Object} row - MedicalContractItem populado con practice.
     */
    openEditItemDialog(row) {
      this.editingItemId = row._id;
      const practiceId = row.practice ? row.practice._id : "";
      const label = row.practice
        ? `${row.practice.code} - ${row.practice.name}`
        : "";
      this.itemForm = {
        practice: practiceId,
        practiceLabel: label,
        isEnabled: !!row.isEnabled,
        providerCode: row.providerCode || "",
        providerName: row.providerName || "",
        // UC-REG-164 — En modo edición el plan queda bloqueado (para
        // cambiar de plan, borrar y crear nuevo).
        plan: row.plan ? row.plan._id || row.plan : null,
        // Los campos de precio se mantienen en el form por consistencia,
        // pero no se envían ni se ven en modo edición.
        amount: 0,
        effectiveFrom: new Date().toISOString().split("T")[0],
        hasCopago: false,
        copagoAmount: 0,
        hasCoseguro: false,
        coseguroPercent: 0,
      };
      this.practiceOptions = practiceId ? [{ value: practiceId, label }] : [];
      this.itemFormErrors = {};
      this.itemDialogVisible = true;
    },

    /**
     * @method saveItem
     * @desc Guarda la prestación en el contrato
     */
    async saveItem() {
      this.itemFormErrors = {};
      if (!this.itemForm.practice) {
        this.itemFormErrors.practice = "Requerido";
        return;
      }
      // UC-REG-164 — Cuando el contrato es por-plan, el plan es obligatorio.
      if (this.perPlanContract && !this.itemForm.plan) {
        this.itemFormErrors.plan = "Requerido";
        return;
      }
      this.itemSaving = true;
      try {
        if (this.editingItemId) {
          // Modo edición — no manda precio (los precios van por el flujo
          // "Precio" que tiene historial). No manda practice ni plan
          // (bloqueados en el UI).
          await medicalContractService.updateItem(
            this.contractId,
            this.editingItemId,
            {
              isEnabled: this.itemForm.isEnabled,
              providerCode: this.itemForm.providerCode
                ? this.itemForm.providerCode.trim()
                : null,
              providerName: this.itemForm.providerName
                ? this.itemForm.providerName.trim()
                : null,
            }
          );
          this.$message.success("Prestación actualizada");
        } else {
          await medicalContractService.upsertItem(this.contractId, {
            practice: this.itemForm.practice,
            plan: this.perPlanContract ? this.itemForm.plan : null,
            isEnabled: this.itemForm.isEnabled,
            providerCode: this.itemForm.providerCode
              ? this.itemForm.providerCode.trim()
              : null,
            providerName: this.itemForm.providerName
              ? this.itemForm.providerName.trim()
              : null,
            price: {
              amount: this.itemForm.amount,
              effectiveFrom: this.itemForm.effectiveFrom,
              hasCopago: this.itemForm.hasCopago,
              copagoAmount: this.itemForm.copagoAmount,
              hasCoseguro: this.itemForm.hasCoseguro,
              coseguroPercent: this.itemForm.coseguroPercent,
            },
          });
          this.$message.success("Prestación agregada");
        }
        this.itemDialogVisible = false;
        this.fetchItems();
      } catch (err) {
        this.$message.error(
          err?.response?.data?.error || "Error al guardar la prestación"
        );
      } finally {
        this.itemSaving = false;
      }
    },

    /**
     * @method addPlanToPractice
     * @desc UC-REG-164 — Abre el modal en modo "Agregar" pre-cargando la
     *   práctica (para agregar otro plan a una práctica ya presente en
     *   el contrato). Se llama desde el botón "+ Agregar plan" dentro
     *   del detalle expandido de una fila.
     * @param {Object} group - Grupo de items del computed groupedItems
     */
    /**
     * @method toggleGroup
     * @desc UC-REG-164 — Alterna el expandido de una práctica en el
     *   modo por-plan. Reemplaza el type="expand" de Element UI que
     *   rendereaba mal el slot dentro de la primera celda.
     */
    /**
     * @method hasConditions
     * @desc true si el precio vigente del item tiene copago o coseguro
     *   (para decidir si renderizar los chips en la card).
     */
    hasConditions(item) {
      const p = this.getCurrentPrice(item);
      return !!(p && (p.hasCopago || p.hasCoseguro));
    },

    /**
     * @method removePractice
     * @desc UC-REG-164 — Borra todos los items (uno por plan) de una
     *   práctica del contrato. Usa el DELETE existente item por item.
     */
    async removePractice(group) {
      const ok = await this.$confirm(
        `Borrar la prestación de todos sus planes (${group.plans.length})?`,
        "Confirmar",
        { type: "warning" }
      ).catch(() => false);
      if (!ok) return;
      try {
        for (const item of group.plans) {
          // eslint-disable-next-line no-await-in-loop
          await medicalContractService.removeItem(this.contractId, item._id);
        }
        this.$message.success("Prestación eliminada");
        this.fetchItems();
      } catch {
        this.$message.error("Error al eliminar la prestación");
      }
    },

    toggleGroup(practiceId) {
      this.$set(
        this.expandedGroups,
        practiceId,
        !this.expandedGroups[practiceId]
      );
    },
    isExpanded(practiceId) {
      return !!this.expandedGroups[practiceId];
    },
    addPlanToPractice(group) {
      this.openItemDialog({
        practiceId: group.practiceId,
        practiceLabel: `${group.code} - ${group.name}`,
        providerCode: group.providerCode,
        providerName: group.providerName,
      });
    },

    /**
     * @method planLabelOfItem
     * @desc Nombre del plan de un item. Si no tiene plan (contrato
     *   sameValueForAllPlans=true) muestra "Todos los planes".
     */
    planLabelOfItem(item) {
      if (!item.plan) return "Todos los planes";
      if (typeof item.plan === "object" && item.plan.name)
        return item.plan.name;
      const found = this.planOptions.find((o) => o.value === item.plan);
      return found ? found.label : "—";
    },

    /**
     * @method summaryOfGroup
     * @desc UC-REG-164 — Resumen de planes cubiertos y rango de precios
     *   para mostrar en la fila principal de la tabla expandible.
     * @param {Object} group
     * @returns {string} ej. "3 de 4 cubiertos · $12k–$15k"
     */
    summaryOfGroup(group) {
      if (!this.perPlanContract) return "";
      const enabled = group.plans.filter((p) => p.isEnabled);
      const prices = enabled
        .map((p) => {
          const cp = this.getCurrentPrice(p);
          return cp ? cp.amount : null;
        })
        .filter((n) => n !== null);
      const cover = `${enabled.length} de ${group.plans.length} cubiertos`;
      if (prices.length === 0) return `${cover} · sin precio`;
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      const fmt = (n) =>
        n >= 1000
          ? `$${(n / 1000).toFixed(0)}k`
          : `$${n.toLocaleString("es-AR")}`;
      const range = min === max ? fmt(min) : `${fmt(min)}–${fmt(max)}`;
      return `${cover} · ${range}`;
    },

    /**
     * @method openPriceDialog
     * @desc Abre el dialog para agregar un nuevo precio a una prestación
     * @param {Object} item - Item del contrato
     */
    openPriceDialog(item) {
      this.selectedItem = item;
      this.priceForm = {
        amount: 0,
        effectiveFrom: new Date().toISOString().split("T")[0],
        hasCopago: false,
        copagoAmount: 0,
        hasCoseguro: false,
        coseguroPercent: 0,
      };
      this.priceDialogVisible = true;
    },

    /**
     * @method savePrice
     * @desc Agrega un precio al historial de la prestación
     */
    async savePrice() {
      this.priceSaving = true;
      try {
        await medicalContractService.addPrice(
          this.contractId,
          this.selectedItem._id,
          this.priceForm
        );
        this.$message.success("Precio actualizado");
        this.priceDialogVisible = false;
        this.fetchItems();
      } catch {
        this.$message.error("Error al guardar el precio");
      } finally {
        this.priceSaving = false;
      }
    },

    /**
     * @method toggleItemEnabled
     * @desc Activa/desactiva una prestación en el contrato
     * @param {Object} item
     */
    async toggleItemEnabled(item) {
      try {
        await medicalContractService.updateItem(this.contractId, item._id, {
          isEnabled: !item.isEnabled,
        });
        item.isEnabled = !item.isEnabled;
      } catch {
        this.$message.error("Error al actualizar la prestación");
      }
    },

    /**
     * @method removeItem
     * @desc Elimina una prestación del contrato
     * @param {Object} item
     */
    async removeItem(item) {
      try {
        await this.$confirm(
          "¿Eliminar esta prestación del contrato?",
          "Confirmar",
          {
            confirmButtonText: "Eliminar",
            cancelButtonText: "Cancelar",
            type: "warning",
          }
        );
        await medicalContractService.removeItem(this.contractId, item._id);
        this.$message.success("Prestación eliminada");
        this.fetchItems();
      } catch {
        /* cancelado */
      }
    },

    /**
     * @method getCurrentPrice
     * @desc Obtiene el precio vigente de un item
     * @param {Object} item
     * @returns {Object|null}
     */
    getCurrentPrice(item) {
      if (!item.prices || !item.prices.length) return null;
      const now = new Date();
      return (
        item.prices
          .filter((p) => new Date(p.effectiveFrom) <= now)
          .sort(
            (a, b) => new Date(b.effectiveFrom) - new Date(a.effectiveFrom)
          )[0] || null
      );
    },

    /** @method formatDate */
    formatDate(d) {
      return d ? new Date(d).toLocaleDateString("es-AR") : "-";
    },

    /** @method formatCurrency */
    formatCurrency(n) {
      return n != null ? `$${Number(n).toLocaleString("es-AR")}` : "-";
    },

    /**
     * @method formatShortDate — dd/mm/yy AR para mostrar effectiveFrom
     *   y createdAt de los precios en el card compacto.
     * @param {Date|string} d
     * @returns {string}
     */
    formatShortDate(d) {
      if (!d) return "-";
      return new Date(d).toLocaleDateString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      });
    },

    /** @method goBack */
    goBack() {
      this.$router.push({ name: "contracts" });
    },
  },
};
</script>

<template lang="pug">
div(v-loading="loading")
  .page-header
    .page-header__left
      el-button(type="text" icon="el-icon-arrow-left" @click="goBack") Contratos
      h1.page-title {{ isNew ? 'Nuevo contrato' : (contract ? 'Contrato ' + (contract.contractNumber || '') : '') }}
    el-button(type="primary" :loading="saving" @click="saveContract") {{ isNew ? 'Crear contrato' : 'Guardar cambios' }}

  el-tabs(v-model="activeTab" @tab-click="onTabClick")

    //- Tab datos generales
    el-tab-pane(label="Datos generales" name="general")
      el-form(label-position="top" size="small")
        .form-card
          el-row(:gutter="16")
            el-col(:span="12")
              el-form-item(label="Prestador" required :error="formErrors.provider")
                el-select(
                  v-model="form.provider"
                  filterable
                  remote
                  reserve-keyword
                  placeholder="Buscar prestador..."
                  :remote-method="searchProviders"
                  :loading="providerSearchLoading"
                  style="width: 100%"
                )
                  el-option(v-for="o in providerOptions" :key="o.value" :label="o.label" :value="o.value")
            el-col(:span="12")
              el-form-item(label="N° de contrato")
                el-input(v-model="form.contractNumber" placeholder="Ej: CTR-2026-001")
          el-row(:gutter="16")
            el-col(:span="8")
              el-form-item(label="Estado")
                el-select(v-model="form.status" style="width: 100%")
                  el-option(label="Borrador" value="draft")
                  el-option(label="Activo" value="active")
                  el-option(label="Vencido" value="expired")
                  el-option(label="Rescindido" value="terminated")
            el-col(:span="8")
              el-form-item(label="Fecha de inicio" required :error="formErrors.startsAt")
                el-input(v-model="form.startsAt" type="date" style="width: 100%")
            el-col(:span="8")
              el-form-item(label="Fecha de vencimiento")
                el-input(v-model="form.endsAt" type="date" style="width: 100%")
          el-row(:gutter="16")
            el-col(:span="12")
              el-form-item(label="Modalidad de pago")
                el-select(v-model="form.paymentModality" style="width: 100%")
                  el-option(label="Por prestación" value="per-practice")
                  el-option(label="Mensual" value="monthly")
                  el-option(label="Mixto" value="mixed")
            el-col(:span="12")
              //- UC-REG-163 — Cuál nomenclatura ver en el UI del validador
              //- y las bandejas. Ambos códigos se snapshotean siempre.
              el-form-item(label="Códigos a mostrar")
                el-select(v-model="form.codeDisplayMode" style="width: 100%")
                  el-option(label="Nuestros códigos" value="ours")
                  el-option(label="Códigos del prestador" value="provider")
          el-row(:gutter="16")
            el-col(:span="12")
              el-form-item(label=" ")
                el-checkbox(v-model="form.sameValueForAllPlans") Mismo valor para todos los planes
          el-form-item(label="Notas")
            el-input(v-model="form.notes" type="textarea" :rows="3")

    //- Tab prestaciones
    el-tab-pane(label="Prestaciones" name="items" :disabled="isNew")
      .items-toolbar
        el-input(
          v-model="itemsSearch"
          placeholder="Buscar prestación..."
          prefix-icon="el-icon-search"
          clearable
          size="small"
          style="width: 260px"
          @input="debounceItemsSearch"
          @clear="debounceItemsSearch"
        )
        //- UC-REG-203 — Filtro por plan (solo visible en modo por-plan).
        //- Muestra solo los items del plan elegido, o todos si "Todos".
        el-select(
          v-if="perPlanContract"
          v-model="filterPlan"
          placeholder="Todos los planes"
          clearable
          size="small"
          style="width: 200px; margin-left: 8px"
        )
          el-option(
            v-for="p in planOptions"
            :key="p.value"
            :label="p.label"
            :value="p.value"
          )
        el-button(type="primary" size="small" icon="el-icon-plus" @click="openItemDialog") Agregar prestación

      //- UC-REG-164 rediseño — Lista de cards. Mismo layout para modo
      //- plano (1 item por práctica) y por-plan (N items expandible).
      //- Slot del chevron reservado siempre para que el texto no se
      //- corra al alternar modos o expandir.
      .prest-cards(v-loading="itemsLoading")
        .prest-cards__empty(v-if="!itemsLoading && groupedItems.length === 0")
          | No hay prestaciones cargadas todavía.
        .prest-card(
          v-for="g in groupedItems"
          :key="g.practiceId"
          :class="{ 'is-expanded': isExpanded(g.practiceId) }"
        )
          .prest-card__row
            //- Chevron: solo clickeable en modo por-plan. En plano queda
            //- vacío pero reserva el ancho — el layout no salta.
            .prest-card__chevron(v-if="perPlanContract" @click="toggleGroup(g.practiceId)")
              i.el-icon-arrow-down(v-if="isExpanded(g.practiceId)")
              i.el-icon-arrow-right(v-else)
            .prest-card__chevron(v-else)
            //- UC-REG-164 rediseño (modelo 2) — Dos códigos apilados con
            //- label explícita a la izquierda. "Interno" siempre arriba
            //- y en navy; "Prest." abajo y en gris solo si hay providerCode.
            .prest-card__code
              .prest-card__code-row
                span.prest-card__code-label Interno
                span.prest-card__code-main {{ g.code }}
              .prest-card__code-row(v-if="g.providerCode")
                span.prest-card__code-label Prest.
                span.prest-card__code-alt {{ g.providerCode }}
            .prest-card__name
              .prest-card__name-main {{ g.name }}
              .prest-card__name-sub(v-if="perPlanContract") {{ summaryOfGroup(g) }}
              .prest-card__name-sub(v-else-if="g.providerName") {{ g.providerName }}
            //- En modo plano: precio + chips + toggle + botones.
            //- En por-plan: solo botón borrar (el resto va en cada plan).
            template(v-if="!perPlanContract")
              .prest-card__meta
                .prest-card__price(v-if="getCurrentPrice(g.plans[0])") {{ formatCurrency(getCurrentPrice(g.plans[0]).amount) }}
                .prest-card__price.is-empty(v-else) Sin precio
                //- UC-REG-194 — fechas del precio vigente (effectiveFrom + createdAt).
                //-   Antes solo se veía el monto; ahora Federico sabe si el
                //-   precio es viejo/nuevo y desde cuándo aplica.
                .prest-card__price-dates(v-if="getCurrentPrice(g.plans[0])")
                  | Vig. {{ formatShortDate(getCurrentPrice(g.plans[0]).effectiveFrom) }}
                  span(v-if="getCurrentPrice(g.plans[0]).createdAt")
                    |  · cargado {{ formatShortDate(getCurrentPrice(g.plans[0]).createdAt) }}
                  span(v-if="(g.plans[0].prices || []).length > 1")
                    |  · {{ (g.plans[0].prices || []).length }} precios en historial
                .prest-card__chips(v-if="hasConditions(g.plans[0])")
                  span.prest-card__chip(v-if="getCurrentPrice(g.plans[0]).hasCopago") Copago {{ formatCurrency(getCurrentPrice(g.plans[0]).copagoAmount) }}
                  span.prest-card__chip(v-if="getCurrentPrice(g.plans[0]).hasCoseguro") Coseguro {{ getCurrentPrice(g.plans[0]).coseguroPercent }}%
              .prest-card__actions
                el-tooltip(content="Habilitada" placement="top")
                  el-switch(:value="g.plans[0].isEnabled" @change="toggleItemEnabled(g.plans[0])")
                el-tooltip(content="Editar" placement="top")
                  el-button(size="mini" icon="el-icon-edit" circle @click="openEditItemDialog(g.plans[0])")
                el-tooltip(content="Precio" placement="top")
                  el-button(size="mini" icon="el-icon-price-tag" circle @click="openPriceDialog(g.plans[0])")
                el-tooltip(content="Borrar" placement="top")
                  el-button(size="mini" type="danger" icon="el-icon-delete" circle @click="removeItem(g.plans[0])")
            template(v-else)
              .prest-card__actions
                el-tooltip(content="Borrar toda la práctica" placement="top")
                  el-button(size="mini" type="danger" icon="el-icon-delete" circle @click="removePractice(g)")

          //- Sub-lista de planes (solo por-plan expandido)
          .prest-card__plans(v-if="perPlanContract && isExpanded(g.practiceId)")
            .prest-plan-row(v-for="item in g.plans" :key="item._id")
              .prest-plan-row__name(:class="{ 'is-off': !item.isEnabled }") {{ planLabelOfItem(item) }}
              .prest-plan-row__price
                span(v-if="getCurrentPrice(item)") {{ formatCurrency(getCurrentPrice(item).amount) }}
                span.is-off(v-else-if="!item.isEnabled") No cubierta
                span.is-off(v-else) Sin precio
                //- UC-REG-194 — fechas del precio vigente por plan.
                .prest-plan-row__dates(v-if="getCurrentPrice(item)")
                  | Vig. {{ formatShortDate(getCurrentPrice(item).effectiveFrom) }}
                  span(v-if="getCurrentPrice(item).createdAt")
                    |  · cargado {{ formatShortDate(getCurrentPrice(item).createdAt) }}
                  span(v-if="(item.prices || []).length > 1")
                    |  · {{ (item.prices || []).length }} en historial
              .prest-plan-row__chips
                span.prest-card__chip(v-if="getCurrentPrice(item) && getCurrentPrice(item).hasCopago") Copago {{ formatCurrency(getCurrentPrice(item).copagoAmount) }}
                span.prest-card__chip(v-if="getCurrentPrice(item) && getCurrentPrice(item).hasCoseguro") Coseguro {{ getCurrentPrice(item).coseguroPercent }}%
              .prest-plan-row__actions
                el-switch(:value="item.isEnabled" @change="toggleItemEnabled(item)")
                el-button(size="mini" icon="el-icon-edit" circle @click="openEditItemDialog(item)")
                el-button(size="mini" icon="el-icon-price-tag" circle @click="openPriceDialog(item)")
                el-button(size="mini" type="danger" icon="el-icon-delete" circle @click="removeItem(item)")
            el-button.prest-card__add-plan(size="mini" type="text" icon="el-icon-plus" @click="addPlanToPractice(g)") Agregar plan

      .pagination-wrapper(style="margin-top: 16px; text-align: center")
        el-pagination(
          v-if="itemsTotal > itemsPageSize"
          layout="prev, pager, next, total"
          :total="itemsTotal"
          :page-size="itemsPageSize"
          :current-page="itemsPage"
          @current-change="p => { itemsPage = p; fetchItems() }"
        )

  //- Dialog agregar prestación
  //- UC-REG-164 — El dialog sirve para agregar Y editar. Título dinámico +
  //- práctica/plan bloqueados en modo edit + sección precio oculta en edit
  //- (los precios van por el flow "Precio" con historial).
  el-dialog(:title="editingItemId ? 'Editar prestación' : 'Agregar prestación'" :visible.sync="itemDialogVisible" width="720px")
    el-form(label-position="top" size="small")
      el-form-item(label="Prestación" required :error="itemFormErrors.practice")
        el-select(
          v-model="itemForm.practice"
          :disabled="!!editingItemId"
          filterable
          remote
          reserve-keyword
          placeholder="Buscar por código o nombre..."
          :remote-method="searchPractices"
          :loading="practiceSearchLoading"
          style="width: 100%"
        )
          el-option(v-for="o in practiceOptions" :key="o.value" :label="o.label" :value="o.value")
      //- UC-REG-164 — Selector de plan solo cuando el contrato es
      //- sameValueForAllPlans=false. En edit el plan queda bloqueado.
      el-form-item(v-if="perPlanContract" label="Plan" required :error="itemFormErrors.plan")
        el-select(
          v-model="itemForm.plan"
          :disabled="!!editingItemId"
          filterable
          placeholder="Elegir plan"
          style="width: 100%"
        )
          el-option(v-for="o in planOptions" :key="o.value" :label="o.label" :value="o.value")
      el-form-item(label=" ")
        el-checkbox(v-model="itemForm.isEnabled") Prestador la brinda
      //- UC-REG-167 — dar más ancho a "Denominación" (hasta 180 chars);
      //- "Código" es corto y se conforma con menos espacio.
      el-row(:gutter="12")
        el-col(:span="8")
          el-form-item(label="Código según prestador (opcional)")
            el-input(v-model="itemForm.providerCode" placeholder="Ej: EXT-01" maxlength="60" show-word-limit)
        el-col(:span="16")
          el-form-item(label="Denominación según prestador (opcional)")
            el-input(v-model="itemForm.providerName" placeholder="Ej: EXTRACCIÓN OJO CUERPO" maxlength="180" show-word-limit)
      //- UC-REG-164 — Precio inicial solo en modo agregar. En edit los
      //- precios se manejan aparte con el botón "Precio" (historial).
      .form-card(v-if="!editingItemId")
        .form-card__title Precio inicial
        el-row(:gutter="12")
          el-col(:span="12")
            el-form-item(label="Precio base $")
              el-input-number(v-model="itemForm.amount" :min="0" :precision="2" style="width: 100%")
          el-col(:span="12")
            el-form-item(label="Vigente desde")
              el-input(v-model="itemForm.effectiveFrom" type="date" style="width: 100%")
        el-row(:gutter="12")
          el-col(:span="8")
            el-form-item(label=" ")
              el-checkbox(v-model="itemForm.hasCopago") Copago
          el-col(:span="8")
            el-form-item(label="Monto copago $")
              el-input-number(v-model="itemForm.copagoAmount" :min="0" :precision="2" style="width: 100%" :disabled="!itemForm.hasCopago")
        el-row(:gutter="12")
          el-col(:span="8")
            el-form-item(label=" ")
              el-checkbox(v-model="itemForm.hasCoseguro") Coseguro
          el-col(:span="8")
            el-form-item(label="% Coseguro")
              el-input-number(v-model="itemForm.coseguroPercent" :min="0" :max="100" style="width: 100%" :disabled="!itemForm.hasCoseguro")
    span(slot="footer")
      el-button(@click="itemDialogVisible = false") Cancelar
      el-button(type="primary" :loading="itemSaving" @click="saveItem") Agregar

  //- Dialog nuevo precio
  el-dialog(title="Actualizar precio" :visible.sync="priceDialogVisible" width="480px")
    p(v-if="selectedItem" style="color: #666; margin-bottom: 16px")
      | {{ selectedItem.practice ? selectedItem.practice.code + ' - ' + selectedItem.practice.name : '' }}
    el-form(label-position="top" size="small")
      el-row(:gutter="12")
        el-col(:span="12")
          el-form-item(label="Precio base $")
            el-input-number(v-model="priceForm.amount" :min="0" :precision="2" style="width: 100%")
        el-col(:span="12")
          el-form-item(label="Vigente desde")
            el-input(v-model="priceForm.effectiveFrom" type="date" style="width: 100%")
      el-row(:gutter="12")
        el-col(:span="8")
          el-form-item(label=" ")
            el-checkbox(v-model="priceForm.hasCopago") Copago
        el-col(:span="8")
          el-form-item(label="Monto $")
            el-input-number(v-model="priceForm.copagoAmount" :min="0" :precision="2" style="width: 100%" :disabled="!priceForm.hasCopago")
      el-row(:gutter="12")
        el-col(:span="8")
          el-form-item(label=" ")
            el-checkbox(v-model="priceForm.hasCoseguro") Coseguro
        el-col(:span="8")
          el-form-item(label="% Coseguro")
            el-input-number(v-model="priceForm.coseguroPercent" :min="0" :max="100" style="width: 100%" :disabled="!priceForm.hasCoseguro")
    span(slot="footer")
      el-button(@click="priceDialogVisible = false") Cancelar
      el-button(type="primary" :loading="priceSaving" @click="savePrice") Guardar precio
</template>

<style scoped>
/* UC-REG-164 rediseño — lista de cards para prestaciones del contrato.
   Layout coherente entre modo plano (sameValueForAllPlans=true, 1 item
   por card) y modo por-plan (N items expandibles). El slot del chevron
   se reserva siempre para que el texto no se corra al alternar modos. */
.prest-cards {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 60px;
}
.prest-cards__empty {
  text-align: center;
  color: #94a3b8;
  padding: 40px 0;
  font-size: 13px;
}
.prest-card {
  background: white;
  border: 0.5px solid #e2e8f0;
  border-radius: 10px;
  transition: box-shadow 0.15s;
}
.prest-card:hover {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
.prest-card.is-expanded {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}
.prest-card__row {
  display: grid;
  grid-template-columns: 28px 130px 1fr auto auto;
  gap: 14px;
  align-items: center;
  padding: 12px 16px;
}
.prest-card__chevron {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.15s;
}
.prest-card__chevron:empty {
  cursor: default;
}
.prest-card__chevron:not(:empty):hover {
  background: #f1f5f9;
}
/* UC-REG-164 rediseño (modelo 2). Dos filas apiladas: cada una con label
   tipográfica ("Interno" / "Prest.") a la izquierda y el código a la
   derecha. Label a 42px fija alinea verticalmente los dos códigos. */
.prest-card__code {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-variant-numeric: tabular-nums;
}
.prest-card__code-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
}
.prest-card__code-label {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #94a3b8;
  font-weight: 500;
  min-width: 42px;
}
.prest-card__code-main {
  font-size: 14px;
  font-weight: 500;
  color: #002d5e;
}
.prest-card__code-alt {
  font-size: 12px;
  color: #64748b;
}
.prest-card__name-main {
  font-size: 13px;
  color: #1a1f36;
  line-height: 1.35;
}
.prest-card__name-sub {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 3px;
  font-style: italic;
}
.prest-card__meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}
.prest-card__price {
  font-size: 14px;
  font-weight: 500;
  color: #1a1f36;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.prest-card__price.is-empty {
  color: #cbd5e1;
  font-weight: 400;
  font-style: italic;
  font-size: 12px;
}
/* UC-REG-194 — fechas del precio (Vig. + cargado + historial) */
.prest-card__price-dates {
  font-size: 10.5px;
  color: #94a3b8;
  margin-top: 2px;
  line-height: 1.3;
  white-space: nowrap;
}
.prest-plan-row__dates {
  font-size: 10.5px;
  color: #94a3b8;
  margin-top: 2px;
  line-height: 1.3;
  white-space: nowrap;
  font-weight: 400;
}
.prest-card__chips {
  display: flex;
  gap: 4px;
}
.prest-card__chip {
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #64748b;
  font-weight: 500;
  white-space: nowrap;
}
.prest-card__actions {
  display: flex;
  gap: 6px;
  align-items: center;
}
.prest-card__plans {
  border-top: 0.5px solid #e2e8f0;
  padding: 8px 16px 12px calc(28px + 14px + 16px);
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: #fafbfc;
  border-radius: 0 0 10px 10px;
}
.prest-plan-row {
  display: grid;
  grid-template-columns: 200px 110px 1fr auto;
  gap: 12px;
  align-items: center;
  font-size: 12px;
  padding: 8px 4px;
  border-bottom: 0.5px solid #f1f5f9;
}
.prest-plan-row:last-of-type {
  border-bottom: none;
}
.prest-plan-row__name {
  color: #1a1f36;
  font-weight: 500;
}
.prest-plan-row__name.is-off {
  color: #94a3b8;
  font-style: italic;
  font-weight: 400;
}
.prest-plan-row__price {
  font-variant-numeric: tabular-nums;
  color: #1a1f36;
}
.prest-plan-row__price .is-off {
  color: #94a3b8;
  font-style: italic;
}
.prest-plan-row__chips {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}
.prest-plan-row__actions {
  display: flex;
  gap: 6px;
  align-items: center;
}
.prest-card__add-plan {
  margin-top: 6px !important;
  align-self: flex-start;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.page-header__left {
  display: flex;
  align-items: center;
  gap: 8px;
}
.page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}
.items-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.form-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}
.form-card__title {
  font-weight: 600;
  margin-bottom: 12px;
  color: #303133;
}
</style>
